import { useNavigate } from 'react-router-dom';
import { useReveal } from '@/hooks/useReveal';
import PageLoader from '@/components/PageLoader';
import HeroCrumbs from '@/components/HeroCrumbs';
import Button from '@/components/ui/Button';
import { PRODUCT_PAGE, PRODUCT_DETAILS, CASE_POSTS } from '@/data/mockData';
import { GUIDE_ICON } from '@/components/GuideIcons';
// [2026-10-23 사용자] 제품 영역 화이트 전환 파일럿 — evs-g200만 화이트 전용 모듈로 격리.
//   다크 ProductEvsG200.module(instruments 등 공유)은 건드리지 않음. 클래스명 1:1이라 본문 무수정, import만 교체.
//   통과 시 나머지 제품 페이지도 동일 전환 예정. 상세=ProductWhite.module.scss 헤더.
import s from './ProductWhite.module.scss';
// [2026-10-23 사용자] ② PRODUCT ROLE을 '실시간 관제' 셰브론 스타일로 → SafetyEnv(화이트)의 w.chevron 재사용.
//   evs-g200도 화이트라 동일 톤. 이 셰브론이 이 페이지의 유일 다이어그램(페이지당 1개 원칙).
import w from './SafetyEnv.module.scss';

// ============================================================================
//  제품 상세 — EVS-G200 IoT 게이트웨이  (/products/evs-g200)
//  [2026-10-08 사용자] 회사 기획서 v7(2-2) '제품별 독립 URL 상세페이지' 승격 1호.
//    문서가 지정한 10개 콘텐츠 영역을 '위→아래 고정 순서'로 배치(= block 65-75),
//    공통규칙(block 444-455): 첫화면=제목·한줄가치·대표이미지·핵심버튼 / 반복 CTA(상·중·하, 3종 이내).
//  자료 유무 원칙(사용자 합의): 자료 없는 섹션도 '자리 + 실무 설명문구' 유지, 이미지/수치는 placeholder·'준비중'.
//    - 실데이터: ①첫화면 ②역할 ③모델 ⑥사양 ⑦연동계측기 ⑨설치사례  (mockData 실측)
//    - 준비중(회사 자료 대기): ④KC인증(증서·번호·발급일) ⑤환경공단 문서 ⑧현장 구성도  (block 83·443 확인 필요)
//  참고: 섹션 골격은 이후 다른 제품 상세로 일반화 예정(공통 컴포넌트화는 Phase 0/후속).
//  근거 데이터: PRODUCT_PAGE.gateway / PRODUCT_DETAILS.g200.features / PRODUCT_PAGE.instruments / CASE_POSTS.
// ============================================================================

const HERO_IMG = '/images/products/products-hero.webp';  // [2026-10-11] 제품 메뉴 히어로 이미지 통일(=/products 전체제품과 동일). 구 hero-environment에서 변경
// [2026-10-11 사용자] 서브 슬로건 풍성화(문서 대표제목 핵심 유지+확장). 히어로 중앙 충분히 차게.
const SLOGAN = '현장 계측기의 측정 데이터를 수집·표시·저장하고, Ethernet·LTE로 환경부 GreenLink까지 안정적으로 전송하는 IoT 게이트웨이입니다.';

// 반복 CTA — 문서 block 448: 같은 목적 버튼을 상·중·하 반복, 종류 3개 이내.
//   견적 요청(주행동) / 현장실사 / 카탈로그. 카탈로그 PDF는 자료 준비중 → 비활성 표기.
function CtaRow({ nav }: { nav: (to: string) => void }) {
  return (
    <div className={s.ctaRow}>
      <Button variant="pill" onClick={() => nav('/support/inquiry?product=EVS-G200&from=/products/evs-g200')}>견적 요청</Button>
      <a className={s.ctaGhost} onClick={() => nav('/support/consult')}>현장실사 상담</a>
      <span className={`${s.ctaGhost} ${s.ctaSoon}`} aria-disabled title="카탈로그 PDF 준비중">카탈로그 (준비중)</span>
    </div>
  );
}

// '준비중' 자리표시 — 자료(이미지·증서·구성도) 미보유 섹션. 설명 문구는 유지하고 비주얼만 placeholder.
function Soon({ label }: { label: string }) {
  return (
    <div className={s.soon} role="img" aria-label={`${label} 준비중`}>
      <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
        <circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" />
      </svg>
      <span>{label} · 준비중</span>
    </div>
  );
}

const Reveal = ({ className = '', children }: { className?: string; children: React.ReactNode }) => {
  const ref = useReveal<HTMLDivElement>();
  return <div className={`${className} anim-scroll`} ref={ref}>{children}</div>;
};

export default function ProductEvsG200() {
  const gw = PRODUCT_PAGE.gateway;
  const features = PRODUCT_DETAILS.g200?.features ?? [];
  const instruments = PRODUCT_PAGE.instruments.items;
  const envCases = CASE_POSTS.filter((p) => p.domain === 'environment' && p.category === '설치').slice(0, 6);
  const navigate = useNavigate();
  const nav = (to: string) => navigate(to);

  const FEAT_ICON: Record<string, React.ReactNode> = {
    comm: <><path d="M5 12.5a10 10 0 0 1 14 0" /><path d="M8 15.5a6 6 0 0 1 8 0" /><path d="M12 18.5h.01" /></>,
    gauge: <><path d="M20.5 12a8.5 8.5 0 1 0-17 0" /><path d="M12 12l4-2.5" /><path d="M12 3.5v1" /></>,
    screen: <><rect x="3" y="4" width="18" height="12" rx="1.5" /><path d="M8 20h8M12 16v4" /></>,
    cert: <><path d="M12 3l7 3v5c0 4-3 7-7 8-4-1-7-4-7-8V6z" /><path d="M9 12l2 2 4-4.5" /></>,
  };

  // 모델 비교(3) — 모델명·적용현장은 사양 기반 실무 기술. 채널 상세는 기술팀 확인 전이라 '확인중' 표기(block 83).
  const models = [
    { name: 'EVS-G200', type: '단수형', site: '단일 방지·배출시설 사업장', ch: '확인중' },
    { name: 'EVS-G200D', type: '복수형', site: '방지·배출시설 다수 사업장', ch: '확인중' },
    { name: 'EVS-G200', type: '교대형', site: '교대 운전 시설 사업장', ch: '확인중' },
  ];
  // [2026-10-23 사용자] 모델 카드 색 테마 — 참고 이미지(블루 그라데이션 번호카드)의 '연한→진한 블루' 진행.
  //   단수형=연한 시안(라이트) / 복수형=브랜드 시안(추천 강조) / 교대형=딥블루. 색은 --mcXxx 변수로 카드에 주입.
  //   번호(1·2·3)는 내용상 제외(순차 단계 아님 → 순서·등급 오독 방지). 아이콘=규모 의미(게이트웨이/순환/교대).
  const MODEL_THEME: { i: string; rec: boolean; vars: React.CSSProperties }[] = [
    { i: 'gateway', rec: false, vars: { '--mcBg': '#eaf6fd', '--mcText': '#1b2430', '--mcAccent': '#00AEEF', '--mcChipBg': 'rgba(0,174,239,.12)', '--mcChipTx': '#0481b4', '--mcSub': '#5b6472', '--mcLine': '#d5e8f5' } as React.CSSProperties },
    { i: 'cycle', rec: true, vars: { '--mcBg': '#00AEEF', '--mcText': '#ffffff', '--mcAccent': '#ffffff', '--mcChipBg': 'rgba(255,255,255,.22)', '--mcChipTx': '#ffffff', '--mcSub': 'rgba(255,255,255,.85)', '--mcLine': 'rgba(255,255,255,.28)' } as React.CSSProperties },
    { i: 'toggle', rec: false, vars: { '--mcBg': '#0a4a74', '--mcText': '#ffffff', '--mcAccent': '#7fd4f5', '--mcChipBg': 'rgba(255,255,255,.16)', '--mcChipTx': '#ffffff', '--mcSub': 'rgba(255,255,255,.80)', '--mcLine': 'rgba(255,255,255,.22)' } as React.CSSProperties },
  ];
  // [2026-10-23 사용자] 상세 사양 행별 심플 라인 아이콘 — 사양 키→아이콘 매핑(미매칭=solution 폴백).
  const SPEC_ICON: Record<string, string> = {
    'CPU': 'ai', 'Memory': 'database', 'Storage': 'database', 'O/S': 'monitor',
    '광대역 통신': 'signal', '근거리 통신': 'cycle', '전류센서': 'gauge', '전원': 'energy',
    'Display': 'display', '디스플레이': 'display', '통신': 'signal', '인증': 'certified',
    '크기': 'toggle', '무게': 'toggle', '동작온도': 'gauge', '온도': 'gauge', '방수방진': 'droplet',
  };

  return (
    <main className={s.page}>
      <PageLoader />

      {/* 히어로 — 페이지 제목 + 대표 제목(한 줄 가치) */}
      <header className={s.hero}>
        <div className={s.heroBg} style={{ backgroundImage: `url(${HERO_IMG})` }} />
        <div className={s.heroOverlay} />
        <HeroCrumbs items={['HOME', '제품', 'EVS-G200']} />
        <div className={s.heroInner}>
          <p className={s.heroEyebrow}>{gw.tag}</p>
          <h1 className={s.heroTitle}>EVS-G200</h1>
          <p className={s.heroSub}>{SLOGAN}</p>
        </div>
      </header>

      {/* ① 제품 첫 화면 — 제품사진 · 한 줄 설명 · 단수/복수/교대형 · 견적버튼 */}
      <section className={s.section}>
        <Reveal className={s.first}>
          <div className={s.firstMedia}>
            <img src={gw.image} alt="EVS-G200 IoT 게이트웨이" />
          </div>
          <div className={s.firstBody}>
            <span className={s.badge}>{gw.approval}</span>
            <h2 className={s.h2}>{gw.name}</h2>
            <p className={s.lead}>{gw.desc}</p>
            <div className={s.chips}>
              {gw.variants.map((v, i) => <span key={i} className={s.chip}><b>{v.name}</b>{v.type}</span>)}
            </div>
            <CtaRow nav={nav} />
          </div>
        </Reveal>
      </section>

      {/* ② 제품 역할 — 수집 → 표시 → 저장 → VPN·GreenLink 전송 흐름 */}
      <section className={`${s.section} ${s.alt}`}>
        <Reveal>
          <p className={s.eyebrow}>PRODUCT ROLE</p>
          <h2 className={s.h2c}>계측 데이터를 수집해 환경부 GreenLink까지 전송합니다</h2>
          {/* [2026-10-23 사용자] 단순 카드 흐름 → '실시간 관제' 셰브론(맞물린 컬러 화살표)으로 교체. w.chevron 재사용. */}
          <div className={w.chevron}>
            {[
              { n: '01', t: '수집', i: 'gauge', d: '계측기(전류·차압·온도·pH)의 4~20mA·RS-485 신호를 실시간 수집', c: w.cBlue },
              { n: '02', t: '표시', i: 'monitor', d: '7인치 터치 LCD로 현장에서 측정값을 즉시 확인', c: w.cAmber },
              { n: '03', t: '저장', i: 'database', d: '현장 로컬 저장으로 통신 장애 시에도 데이터 보존', c: w.cIndigo },
              { n: '04', t: '전송', i: 'signal', d: 'Ethernet·LTE(VPN)로 환경부 GreenLink에 자동 전송', c: w.cTeal },
            ].map((x) => (
              <div key={x.n} className={`${w.chevronStep} ${x.c}`}>
                <div className={w.chevronIco}>{GUIDE_ICON[x.i]}</div>
                <span className={w.chevronNum}>{x.n}</span>
                <h3>{x.t}</h3><p>{x.d}</p>
              </div>
            ))}
          </div>
          {/* 문서 2-2 §2 연결·기능: '환경 솔루션' */}
          <a className={s.moreLink} onClick={() => nav('/environment')}>환경 솔루션에서 보기 →</a>
        </Reveal>
      </section>

      {/* ③ 모델 비교 — 단수·복수·교대형 */}
      <section className={s.section}>
        <Reveal>
          <p className={s.eyebrow}>MODELS</p>
          <h2 className={s.h2c}>현장 규모에 맞춘 3가지 모델</h2>
          {/* [2026-10-23 사용자] 밋밋한 표 → 블루 그라데이션 인포그래픽 카드(제안A). 번호는 내용상 제외(순차 아님). */}
          <div className={s.modelCards}>
            {models.map((m, i) => {
              const th = MODEL_THEME[i];
              return (
                <div key={i} className={s.modelCard} style={th.vars}>
                  {th.rec && <span className={s.mcBadge}>다수 현장 추천</span>}
                  <div className={s.mcIco}>{GUIDE_ICON[th.i]}</div>
                  <span className={s.mcType}>{m.type}</span>
                  <h3 className={s.mcName}>{m.name}</h3>
                  <p className={s.mcSite}>{m.site}</p>
                  <div className={s.mcRows}>
                    <div className={s.mcRow}><span>계측 채널</span><span>{m.ch}</span></div>
                    <div className={s.mcRow}><span>인증</span><span>✓ 환경공단 승인</span></div>
                  </div>
                </div>
              );
            })}
          </div>
          <p className={s.note}>※ 교대형 모델명·채널수는 기술팀 확정 후 반영합니다(현재 확인중).</p>
        </Reveal>
      </section>

      {/* 반복 CTA (중간) */}
      <div className={s.ctaBand}><Reveal><CtaRow nav={nav} /></Reveal></div>

      {/* ④ KC 인증 — 자료 준비중 */}
      <section className={`${s.section} ${s.alt}`}>
        <Reveal>
          <p className={s.eyebrow}>KC CERTIFICATION</p>
          <h2 className={s.h2c}>KC 인증 정보</h2>
          <p className={s.sub}>KC 공식 인증명칭·인증번호·발급일·대상 모델과 증서 원문(PDF)을 이 영역에 게시합니다.</p>
          <Soon label="KC 인증서·인증번호" />
        </Reveal>
      </section>

      {/* ⑤ 환경공단 관련 확인 — 자료 준비중 */}
      <section className={s.section}>
        <Reveal>
          <p className={s.eyebrow}>K-ECO</p>
          <h2 className={s.h2c}>한국환경공단 관련 확인</h2>
          <p className={s.sub}>모델별 승인·확인 공식 명칭과 문서, 적용 전송 프로토콜(GreenLink)을 게시합니다. (공식 명칭은 원문 기준으로 확정)</p>
          <Soon label="환경공단 승인·확인 문서" />
          {/* 문서 2-2 §5 연결·기능: '인증·기술 페이지' */}
          <a className={s.moreLink} onClick={() => nav('/company/certifications')}>인증·기술 페이지 →</a>
        </Reveal>
      </section>

      {/* ⑥ 상세사양 — [2026-10-23 사용자] 밋밋한 표 → 전폭 딥네이비 밴드 + 제원 리스트(아이콘+키/값). 참고 '느낌'을 브랜드색으로. */}
      <section className={`${s.section} ${s.specBand}`}>
        <Reveal>
          <p className={s.eyebrow}>SPECIFICATIONS</p>
          <h2 className={s.h2c}>상세 사양</h2>
          <div className={s.specList}>
            {gw.specs.map((sp, i) => (
              <div key={i} className={s.specRow}>
                <div className={s.specIco}>{GUIDE_ICON[SPEC_ICON[sp.k] || 'solution']}</div>
                <span className={s.specKey}>{sp.k}</span>
                <span className={s.specVal}>{sp.v}</span>
              </div>
            ))}
          </div>
          <ul className={s.featList}>
            {features.map((f, i) => (
              <li key={i} className={s.feat}>
                <span className={s.featIco} aria-hidden>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
                    {FEAT_ICON[f.icon] || FEAT_ICON.cert}
                  </svg>
                </span>
                <span className={s.featTx}><strong>{f.title}</strong><em>{f.desc}</em></span>
              </li>
            ))}
          </ul>
          <p className={s.note}>※ 7인치 1080P 표현·전류센서 채널수는 기술팀 확인 후 확정 게시(block 83).</p>
        </Reveal>
      </section>

      {/* ⑦ 연동 계측기 — 전류·차압·온도·pH */}
      <section className={s.section}>
        <Reveal>
          <p className={s.eyebrow}>INSTRUMENTS</p>
          <h2 className={s.h2c}>4~20mA로 연동되는 계측기 4종</h2>
          <div className={s.instGrid}>
            {instruments.map((it) => (
              <a key={it.name} className={s.instCard} onClick={() => nav('/products/instruments')}>
                <div className={s.instImg}><img src={it.image} alt={it.name} loading="lazy" /></div>
                <strong>{it.name}</strong>
                <em>{it.desc}</em>
              </a>
            ))}
          </div>
        </Reveal>
      </section>

      {/* ⑧ 현장 구성 예시 — 구성도 준비중 */}
      <section className={`${s.section} ${s.alt}`}>
        <Reveal>
          <p className={s.eyebrow}>SITE CONFIGURATION</p>
          <h2 className={s.h2c}>현장 구성 예시</h2>
          <p className={s.sub}>소형(단수)·복수·교대 사업장별 게이트웨이+계측기 구성도와 수량 예시를 게시합니다. 예: 단수형 1대 + 전류·차압계 각 1 / 복수형 1대 + 계측기 다채널.</p>
          <Soon label="현장 구성도" />
          {/* 문서 2-2 §8 연결·기능: '현장실사' */}
          <a className={s.moreLink} onClick={() => nav('/support/consult')}>현장실사 상담 →</a>
        </Reveal>
      </section>

      {/* ⑨ 설치사례 — 대표 환경 설치사례 */}
      <section className={s.section}>
        <Reveal>
          <p className={s.eyebrow}>CASES</p>
          <h2 className={s.h2c}>대표 환경 설치사례</h2>
          <div className={s.caseGrid}>
            {envCases.map((c) => (
              <a key={c.slug} className={s.caseCard} onClick={() => nav(`/cases/${c.slug}`)}>
                <div className={s.caseImg}><img src={c.cover} alt={c.title} loading="lazy" /></div>
                <strong>{c.title}</strong>
                <em>{c.date}</em>
              </a>
            ))}
          </div>
          <a className={s.moreLink} onClick={() => nav('/cases?tab=environment')}>환경 설치사례 전체 보기 →</a>
        </Reveal>
      </section>

      {/* ⑩ 최종 행동 */}
      <section className={`${s.section} ${s.finalCta}`}>
        <Reveal>
          <h2 className={s.h2c}>도입을 검토 중이신가요?</h2>
          <p className={s.sub}>견적 요청·현장실사 상담으로 바로 연결됩니다. 카탈로그·인증서는 준비되는 대로 제공됩니다.</p>
          <CtaRow nav={nav} />
        </Reveal>
      </section>
    </main>
  );
}
